import { BillLineInstallmentV2 } from '@types'
import { DealConditions } from './commercial-relationship'

export interface InvoiceDetail {
  item: string
  quantity: string
  value: string
}
export interface InvoiceContract {
  id: string
  effect_type: string
  effect_strategy: string
  division_method: string
  contract_due_date: string
  expected_settlement_date: string
  receivable_debtor: string
  payment_scheme: string
  amount_cents: string
  status: string
  created_at: string
}

export interface BaseInvoice<Installments = InvoiceInstallments> {
  status: InvoiceStatus
  id: string
  short_id: string
  external_id?: string
  displayable_id: string
  created_at: string
  due_date: string
  buyer_name: string
  seller_company_id: string
  // Back-end error typing;
  deal_condition?: Partial<DealConditions>
  deal_conditions?: number[]
  seller_name: string
  amount_cents: string
  amount_cents_installment?: string
  description: string
  invoice_installments: Installments[]
  details?: InvoiceDetail[]
  contracts?: InvoiceContract[]
}

export type PendingInvoiceInstallments = {
  amount_cents: string
  date: string
}

export enum BillInvoiceInstallmentsMopStatus {
  Canceled = 'CANCELED',
  Pending = 'PENDING',
  Created = 'CREATED',
  Failed = 'FAILED',
  Registered = 'REGISTERED',
  Overdue = 'OVERDUE',
  Paid = 'PAID',
}

export type InvoiceInstallments = {
  id: string
  original_amount_cents?: string
  amount_cents: string
  settlement_date: string
  status: InstallmentStatus
  service_fees?: string
  mop_status?: BillInvoiceInstallmentsMopStatus
  url?: string
  line_code?: string
}

export type MerchantInvoiceInstallments = InvoiceInstallments & {
  buyer_conciliation_status: InstallmentStatus
  buyer_conciliated_amount: string
}

export enum InstallmentStatus {
  Pending = 'pending',
  Confirmed = 'confirmed',
  Manual = 'manual-audit',
  Canceled = 'canceled',
  Edited = 'edited',
  Current = 'current',
  Settled = 'settled',
  PartiallySettled = 'partially-settled',
  FullySettled = 'fully-settled',
}

export type InvoiceStatus =
  | 'pending'
  | 'pending-nofunds'
  | 'approved'
  | 'processing'
  | 'confirmed'
  | 'expired'
  | 'settled'
  | 'awaiting-refund'
  | 'canceled'
  | 'edited'
  | 'error'
  | 'manual-audit'

export enum SellerStatementStatus {
  Pending = 'pending',
  Settled = 'settled',
  Canceled = 'canceled',
}

export interface DownloadSupplierStatementCSVResponse {
  download_url: string
}

// TODO vai vir somente para buyer mesmo?
export type BuyerInvoice = BaseInvoice<MerchantInvoiceInstallments> & {
  original_amount_cents: string
}

export interface BuyerInvoicesResponse {
  invoices: BuyerInvoice[]
  next_page_token?: string
}

export interface BuyerInvoiceByStatusResponse {
  invoice: BuyerInvoice
  contracts: any[] // TODO o que pode vir aqui?
  transactions: any[] // TODO o que pode vir aqui?
}

export type InvoicesQuery = {
  'filter.status': InvoiceStatus[] | InvoiceStatus
  'search.documentNumber'?: string
  page_token?: string
}

export enum VisualizationType {
  Statements = 'statements',
  Receive = 'receive',
}

export interface StatementInvoice {
  id: string
  buyer_name: string
  seller_name: string
  amount_cents: string
  status: InvoiceStatus
  service_fees: string
  invoice_id: string
  settlement_date: string
}

export interface SellerStatementInstallment {
  id: string
  buyer_name: string
  amount_cents: string
  settlement_date: string
  status: InstallmentStatus
  invoice_id: string
}

export interface Statement {
  settlement_date: string
  settled: boolean
  total_amount?: string
  invoice_installments: StatementInvoice[]
}

export interface SellerStatement {
  settlement_date: string
  status: SellerStatementStatus
  amount_cents?: string
  invoice_installments: SellerStatementInstallment[]
}

export enum ErrorTypes {
  INTERNAL_ERROR = 'INTERNAL_ERROR',
}

export type ErrorViolation = {
  error_type: ErrorTypes
  value: number
}

export type ErrorDetail = {
  ['@type']: string
  violations: ErrorViolation[]
}

export type UploadError = {
  error: string
  line: number
}

export type DeleteProps = {
  companyId: string
  status: InvoiceStatus
  id: string
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}

// MERCHANT
export interface BillInvoiceInstallmentsResponse {
  amount_cents: number
  buyer_conciliated_amount: number
  buyer_conciliation_status: InstallmentStatus // @deprecated
  id: string
  line_code: string
  mop_status: BillInvoiceInstallmentsMopStatus
  settlement_date: string
  status: InstallmentStatus
  url: string
  transaction_id: string
}

export interface MerchantInvoiceResponse {
  amount_cents: number
  original_amount_cents: number
  merchant_service_fee_amount_cents: number
  buyer_company_id: string
  buyer_name: string
  created_at: string
  deal_conditions: number[]
  description: string
  due_date: string
  id: string
  installments: number
  invoice_installments: BillInvoiceInstallmentsResponse[]
  seller_company_id: string
  seller_name: string
  short_id: string
  external_id: string
  status: InvoiceStatus
}

export interface MerchantInvoiceToken {
  tax_invoice_number: number
  tax_invoice_access_key: string
  nfe_download_token: string
}

export interface MerchantGetInvoiceResponse {
  invoice: MerchantInvoiceResponse
  line_items: BillLineInstallmentV2[]
  nfe_download_token: string
}

export interface MerchantInvoice extends MerchantInvoiceResponse {
  invoice_installments: BillInvoiceInstallmentsResponse[]
  line_items: BillLineInstallmentV2[]
  nfe_download_token: string
}

export interface Invoice {
  id: string
  nfe_id: string
  notes: string
  invoice_created_at: string
  limit_reservation_created_at: string
  xml_download_url: string
  amount_cents: number
}

export interface InvoiceDetails {
  id: string
  nfe_id: string
  created_at: string
  approved_at: string
  issuer: string
  amount_cents: number
  status: string
  xml_download_url: string
  supplier_company_id: string
}

export type EditInvoicePayload = {
  invoice_id: string
  supplier_company_id: string
  amount_cents: number
  notes?: string
}
